import { Deferred, Head, Link } from "@inertiajs/react"
import { ChevronLeft } from "lucide-react"
import { useTranslation } from "react-i18next"

import { BrandLogo } from "@/components/brand-logo"
import { CrewPulseStream } from "@/components/creator/crew-pulse-stream"
import { PulseRowSkeleton } from "@/components/creator/pulse-row"
import { useCurrentContext } from "@/hooks/use-current-context"
import { usePollOnFocus } from "@/hooks/use-poll-on-focus"
import AppLayout from "@/layouts/app-layout"
import { creatorCrewPath } from "@/routes"
import type { CrewPulse } from "@/types/collab"

interface CrewPulseProps {
  /** Deferred (Inertia) — absent on first paint (skeleton), then `{events}`. */
  crew_pulse?: CrewPulse
}

/**
 * OKS-473 — §06 Crew Pulse FULL STREAM (`/crew/pulse` → `Creator/Crew/Pulse`).
 *
 * The drill-down from the Crew tab root: a "‹ Crew" back header + the full
 * typed-event stream with live-arrival pill (handled by `<CrewPulseStream>`).
 *
 * Liveness: the creator-shell `useRealtimeRefetch` partial-reloads `crew_pulse`
 * on a `CrewPulse::FanoutJob` signal (push when connected); `usePollOnFocus`
 * below is the graceful degrade (refresh on tab focus + a 30s poll) when the
 * socket is unavailable. The server recomputes the stream in the creator's
 * brand context on every tick — no brand data crosses the wire.
 */
export default function CrewPulsePage({ crew_pulse }: CrewPulseProps) {
  const { t } = useTranslation()
  const { currentBrand } = useCurrentContext()
  usePollOnFocus({ only: ["crew_pulse"] })

  return (
    <AppLayout>
      <Head title={t("crew_pulse.stream_title")} />

      <main className="mx-auto flex w-full max-w-2xl flex-col gap-4 py-4">
        <header className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3 px-4">
            <Link
              href={creatorCrewPath()}
              data-testid="crew-pulse-back"
              className="text-muted-foreground hover:text-foreground -ml-1 inline-flex items-center gap-1 py-1 text-sm font-medium"
            >
              <ChevronLeft aria-hidden="true" className="size-4" />
              {t("crew_pulse.back")}
            </Link>
            <BrandLogo
              logoUrl={currentBrand?.logo_url ?? null}
              name={currentBrand?.name ?? ""}
              className="size-8 shrink-0 rounded-full"
              testId="crew-brand-logo"
            />
          </div>
          <div className="px-4">
            <h1 className="text-2xl font-bold tracking-tight">
              {t("crew_pulse.stream_title")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("crew_pulse.subhead")}
            </p>
          </div>
        </header>

        <Deferred
          data="crew_pulse"
          fallback={
            <ul className="divide-border/60 flex flex-col divide-y">
              {Array.from({ length: 5 }).map((_, i) => (
                <PulseRowSkeleton key={i} />
              ))}
            </ul>
          }
        >
          <CrewPulseStream events={crew_pulse?.events ?? []} />
        </Deferred>
      </main>
    </AppLayout>
  )
}
